import type { AffinityType } from './damage.ts';
import type { HeroClass } from './heroes.ts';
import type { GearAttr, GearEffect, GearSlot, Rarity } from './progression.ts';

/*
 * Gear bases, slots and item power (docs/EQUIPO.md, 2026-10-29). A piece is a base (what it is: its slot,
 * its hands, who wears it, its trait and its model), a rarity, rolled attributes and, for rare and up, an
 * effect. Everything that scales a piece goes by its slot's budget (a whole slot is 10000: the weapon of
 * old); a one-handed weapon and an off-hand add up to what a two-handed one is worth.
 */

/** Where a worn piece sits: the hands, the head, the chest, the feet, the neck and two rings. */
export type WornSlot = 'weapon' | 'offhand' | 'head' | 'armor' | 'boots' | 'amulet' | 'ring' | 'ring2';
/** Every worn slot, in the order its pieces count (the three old slots first, so older heroes add up as before). */
export const WORN_SLOTS: WornSlot[] = ['weapon', 'armor', 'amulet', 'offhand', 'head', 'boots', 'ring', 'ring2'];
/** The level a worn slot opens at (the three old slots are open from the start). */
export const SLOT_LEVEL: Record<WornSlot, number> = { weapon: 1, armor: 1, amulet: 1, boots: 3, offhand: 4, head: 6, ring: 26, ring2: 36 };
/** The kind of piece a worn slot takes (both rings take a ring). */
export const wornKind = (w: WornSlot): GearSlot => (w === 'ring2' ? 'ring' : w);

export type Weight = 'cloth' | 'leather' | 'mail' | 'plate';
export const WEIGHTS: Weight[] = ['cloth', 'leather', 'mail', 'plate'];
/** The heaviest a class wears (D&D proficiency): it can wear that and anything lighter. */
export const CLASS_WEIGHT: Record<HeroClass, Weight> = {
  warrior: 'plate',
  paladin: 'plate',
  cleric: 'mail',
  artificer: 'mail',
  barbarian: 'leather',
  ranger: 'leather',
  druid: 'leather',
  mage: 'cloth',
};

/**
 * A base's trait: fixed (it doesn't grow with power), mostly a trade so that choosing is a way to play.
 * Weapons keep single-target damage per second between −10 % and +8 % of the class's usual one.
 * - attackBp: the basic attack's blow (not skills); attackSpeedBp, critBp, hpBp, damageBp, cooldownBp, armorBp: as gear attributes;
 * - rangeBp: the basic attack's reach; melee: the hero fights in melee at this reach;
 * - speedBp: movement; bigFoeBp: harder on elites and bosses; cleaveBp: the blow reaches around its target (the stronger of this and a cleave effect);
 * - stunEvery / stunTicks: every few blows that land stun the target (half on elites, never bosses);
 * - pierce: the blow carries on into the next nightmare behind its target;
 * - blockBp: chance a melee blow lands for half; skillBp: skills hit harder; healBp: heals more;
 * - summonBp: turrets and the automaton last longer; resBp: guards against the piece's own damage type (rolled on the piece).
 */
export interface GearTrait {
  attackBp?: number;
  attackSpeedBp?: number;
  critBp?: number;
  hpBp?: number;
  damageBp?: number;
  cooldownBp?: number;
  armorBp?: number;
  rangeBp?: number;
  melee?: number;
  speedBp?: number;
  bigFoeBp?: number;
  cleaveBp?: number;
  stunEvery?: number;
  stunTicks?: number;
  pierce?: boolean;
  blockBp?: number;
  skillBp?: number;
  healBp?: number;
  summonBp?: number;
  resBp?: number;
  /** A recipe's (docs/SABERES.md §4.4): a basic blow beside a hurt structure of the group mends it by this share. */
  mendBp?: number;
  /** The moss hood: the nightmares that shoot heroes from afar pick another while there is one in reach. */
  shun?: boolean;
  /** The absent's cloak: what its spectre fights in the bastion gives more experience (ABSENT_CLOAK). */
  absent?: boolean;
}

export type GearBaseId =
  // one-handed
  | 'longsword' | 'handaxe' | 'mace' | 'warhammer' | 'wand' | 'sickle' | 'shortbow'
  // two-handed
  | 'greatsword' | 'greataxe' | 'maul' | 'staff' | 'quarterstaff' | 'longbow' | 'heavyCrossbow'
  // off-hand
  | 'shield' | 'offAxe' | 'grimoire' | 'orb' | 'holySymbol' | 'quiver' | 'toolkit' | 'totem'
  // head, chest, feet by weight
  | 'circlet' | 'hood' | 'coif' | 'helm'
  | 'robe' | 'jerkin' | 'hauberk' | 'breastplate'
  | 'clothBoots' | 'leatherBoots' | 'mailBoots' | 'greaves'
  // neck and fingers
  | 'pendant' | 'reliquary' | 'torc' | 'eye' | 'talisman'
  | 'signet' | 'band' | 'stoneRing' | 'eyeRing' | 'wardRing'
  // recipes (docs/SABERES.md §4.4): only the forge of a bastion that knows them makes them
  | 'masonHammer' | 'mossHood' | 'absentCloak';

export interface GearBase {
  id: GearBaseId;
  slot: GearSlot;
  /** Weapons: one or two hands. */
  hands?: 1 | 2;
  /** Who can wear it (absent: anyone, within its weight). */
  classes?: HeroClass[];
  weight?: Weight;
  trait: GearTrait;
  /** The attributes it can roll (absent: its slot's). */
  attrs?: GearAttr[];
  /** A forge recipe (docs/SABERES.md §4.4): it never drops, and only shows once the bastion knows its saber. */
  recipe?: true;
}

const W = (id: GearBaseId, hands: 1 | 2, classes: HeroClass[], trait: GearTrait = {}): GearBase => ({ id, slot: 'weapon', hands, classes, trait });
const O = (id: GearBaseId, classes: HeroClass[], trait: GearTrait, attrs: GearAttr[]): GearBase => ({ id, slot: 'offhand', classes, trait, attrs });
const A = (id: GearBaseId, slot: 'head' | 'armor' | 'boots', weight: Weight, trait: GearTrait): GearBase => ({ id, slot, weight, trait });
const N = (id: GearBaseId, slot: 'amulet' | 'ring', trait: GearTrait): GearBase => ({ id, slot, trait });

/** Twelve ticks a second (the sim's rate), for the few traits measured in time. */
const SEC = 12;

export const GEAR_BASES: Record<GearBaseId, GearBase> = {
  longsword: W('longsword', 1, ['warrior', 'paladin']),
  handaxe: W('handaxe', 1, ['warrior', 'barbarian'], { critBp: 500 }),
  mace: W('mace', 1, ['warrior', 'paladin', 'cleric'], { bigFoeBp: 1000 }),
  warhammer: W('warhammer', 1, ['paladin', 'artificer']),
  wand: W('wand', 1, ['mage'], { attackSpeedBp: 2000, attackBp: -1500 }),
  sickle: W('sickle', 1, ['druid'], { melee: 1000, attackBp: 800, hpBp: 1500 }),
  shortbow: W('shortbow', 1, ['ranger'], { attackSpeedBp: 800, rangeBp: -2500 }),

  greatsword: W('greatsword', 2, ['warrior', 'paladin'], { cleaveBp: 2500, attackSpeedBp: -1000 }),
  greataxe: W('greataxe', 2, ['warrior', 'barbarian']),
  maul: W('maul', 2, ['paladin'], { stunEvery: 4, stunTicks: SEC / 2, attackSpeedBp: -1000 }),
  staff: W('staff', 2, ['mage']),
  quarterstaff: W('quarterstaff', 2, ['cleric', 'druid']),
  longbow: W('longbow', 2, ['ranger']),
  heavyCrossbow: W('heavyCrossbow', 2, ['artificer'], { attackBp: 3000, attackSpeedBp: -2300, pierce: true }),

  shield: O('shield', ['warrior', 'paladin', 'cleric'], { blockBp: 1500 }, ['hp', 'armor', 'resFire', 'resFrost', 'resLightning', 'resAcid', 'resPoison', 'resShadow']),
  offAxe: O('offAxe', ['barbarian'], { attackSpeedBp: 800 }, ['damage', 'attackSpeed', 'crit']),
  grimoire: O('grimoire', ['mage'], { cooldownBp: 600 }, ['cooldown', 'damage', 'hp']),
  orb: O('orb', ['mage'], { skillBp: 800 }, ['damage', 'crit', 'cooldown']),
  holySymbol: O('holySymbol', ['cleric'], { healBp: 1000 }, ['cooldown', 'hp', 'resFire', 'resFrost', 'resLightning', 'resAcid', 'resPoison', 'resShadow']),
  quiver: O('quiver', ['ranger'], { critBp: 600 }, ['damage', 'attackSpeed', 'crit']),
  toolkit: O('toolkit', ['artificer'], { summonBp: 1500 }, ['cooldown', 'hp', 'damage']),
  totem: O('totem', ['druid'], { skillBp: 600, healBp: 600 }, ['cooldown', 'hp', 'damage']),

  circlet: A('circlet', 'head', 'cloth', { cooldownBp: 300 }),
  hood: A('hood', 'head', 'leather', { speedBp: 300 }),
  coif: A('coif', 'head', 'mail', { armorBp: 400 }),
  helm: A('helm', 'head', 'plate', { armorBp: 700, speedBp: -400 }),
  robe: A('robe', 'armor', 'cloth', { cooldownBp: 600 }),
  jerkin: A('jerkin', 'armor', 'leather', { speedBp: 600 }),
  hauberk: A('hauberk', 'armor', 'mail', { armorBp: 800 }),
  breastplate: A('breastplate', 'armor', 'plate', { armorBp: 1500, speedBp: -800 }),
  clothBoots: A('clothBoots', 'boots', 'cloth', { cooldownBp: 300 }),
  leatherBoots: A('leatherBoots', 'boots', 'leather', { speedBp: 300 }),
  mailBoots: A('mailBoots', 'boots', 'mail', { armorBp: 400 }),
  greaves: A('greaves', 'boots', 'plate', { armorBp: 700, speedBp: -400 }),

  pendant: N('pendant', 'amulet', { hpBp: 500 }),
  reliquary: N('reliquary', 'amulet', { cooldownBp: 400 }),
  torc: N('torc', 'amulet', { damageBp: 400 }),
  eye: N('eye', 'amulet', { critBp: 500 }),
  talisman: N('talisman', 'amulet', { resBp: 800 }),
  signet: N('signet', 'ring', { damageBp: 200 }),
  band: N('band', 'ring', { hpBp: 300 }),
  stoneRing: N('stoneRing', 'ring', { cooldownBp: 200 }),
  eyeRing: N('eyeRing', 'ring', { critBp: 300 }),
  wardRing: N('wardRing', 'ring', { resBp: 400 }),

  // The classes that hold a one-handed hammer or mace; −8 % to the blow, and it mends (docs/SABERES.md §4.4).
  masonHammer: { ...W('masonHammer', 1, ['warrior', 'paladin', 'cleric', 'artificer'], { attackBp: -800, mendBp: 3000 }), recipe: true },
  // A leather hood every class but the mage wears; −5 % of life, and the shooters pick someone else.
  mossHood: { ...A('mossHood', 'head', 'leather', { hpBp: -500, shun: true }), recipe: true },
  // A cloth chest for every class; −10 % of life, and its spectre learns more in the bastion's waves.
  absentCloak: { ...A('absentCloak', 'armor', 'cloth', { hpBp: -1000, absent: true }), recipe: true },
};
/** The bases that drop (and the armory counts): every one but the forge recipes, so no draw of a base moves. */
export const GEAR_BASE_IDS = (Object.keys(GEAR_BASES) as GearBaseId[]).filter((id) => !GEAR_BASES[id].recipe);
/** The forge recipes' bases (docs/SABERES.md §4.4). */
export const RECIPE_BASE_IDS = (Object.keys(GEAR_BASES) as GearBaseId[]).filter((id) => GEAR_BASES[id].recipe);

/** What each class's miniature already holds: the base a piece from before bases takes (with no trait). */
export const CLASS_WEAPON: Record<HeroClass, GearBaseId> = {
  warrior: 'longsword',
  paladin: 'warhammer',
  barbarian: 'greataxe',
  cleric: 'quarterstaff',
  mage: 'staff',
  ranger: 'longbow',
  artificer: 'warhammer',
  druid: 'quarterstaff',
};
/** The base an older piece of a non-weapon slot takes: the class's heaviest chest, a pendant. */
export const legacyBase = (cls: HeroClass, slot: GearSlot): GearBaseId =>
  slot === 'weapon' ? CLASS_WEAPON[cls] : slot === 'armor' ? weightBase('armor', CLASS_WEIGHT[cls]) : 'pendant';

/** The base of a slot and weight (head, chest or feet). */
export const weightBase = (slot: 'head' | 'armor' | 'boots', w: Weight): GearBaseId =>
  GEAR_BASE_IDS.find((id) => GEAR_BASES[id].slot === slot && GEAR_BASES[id].weight === w) as GearBaseId;

const WEIGHT_RANK: Record<Weight, number> = { cloth: 0, leather: 1, mail: 2, plate: 3 };
/** Whether a class can wear a base: its class list and its weight. */
export const canWearBase = (cls: HeroClass, id: GearBaseId): boolean => {
  const b = GEAR_BASES[id];
  if (b.classes && !b.classes.includes(cls)) return false;
  return !b.weight || WEIGHT_RANK[b.weight] <= WEIGHT_RANK[CLASS_WEIGHT[cls]];
};
/** The bases a class can wear in a slot, in catalog order. */
export const basesFor = (cls: HeroClass, slot: GearSlot): GearBaseId[] => GEAR_BASE_IDS.filter((id) => GEAR_BASES[id].slot === slot && canWearBase(cls, id));

/**
 * Each slot's budget (bp of a whole slot): what its rolls, its rare effect and its gems are worth. One hand
 * and an off-hand make a two-handed weapon; the whole set is 4.5 slots, 5.5 with both rings.
 */
export const SLOT_BUDGET_BP: Record<GearSlot, number> = { weapon: 10000, offhand: 5000, head: 5000, armor: 10000, boots: 5000, amulet: 10000, ring: 5000 };
export const TWO_HANDED_BUDGET_BP = 15000;
/** A base's budget: its slot's, a two-handed weapon's. */
export const baseBudget = (id: GearBaseId): number => (GEAR_BASES[id].hands === 2 ? TWO_HANDED_BUDGET_BP : SLOT_BUDGET_BP[GEAR_BASES[id].slot]);

/** The attributes a slot rolls (an off-hand rolls its base's). Capped ones (cooldown, armor, affinity) don't grow with power. */
const RES: GearAttr[] = ['resFire', 'resFrost', 'resLightning', 'resAcid', 'resPoison', 'resShadow'];
export const SLOT_ATTRS: Record<GearSlot, GearAttr[]> = {
  weapon: ['damage', 'attackSpeed', 'cooldown', 'crit', 'hp'],
  offhand: [],
  head: ['hp', 'cooldown', 'armor', 'crit', ...RES],
  armor: ['hp', 'armor', 'cooldown', 'attackSpeed', ...RES],
  boots: ['hp', 'armor', 'attackSpeed', ...RES],
  amulet: ['damage', 'hp', 'attackSpeed', 'cooldown', 'armor', 'crit', ...RES],
  ring: ['damage', 'hp', 'attackSpeed', 'cooldown', 'armor', 'crit', ...RES],
};
export const baseAttrs = (id: GearBaseId): GearAttr[] => GEAR_BASES[id].attrs ?? SLOT_ATTRS[GEAR_BASES[id].slot];
/** Attributes that grow with a piece's power (the others have a cap per hero: GEAR_CAPS, AFFINITY). */
export const STRETCHED_ATTRS: GearAttr[] = ['damage', 'hp', 'attackSpeed', 'crit'];

/** The rare effects a slot can carry. */
export const SLOT_EFFECTS: Record<GearSlot, GearEffect[]> = {
  weapon: ['burn', 'lifesteal', 'fury', 'execute', 'chain', 'frost', 'cleave'],
  offhand: ['guard', 'echo', 'thorns', 'banner', 'lifesteal'],
  head: ['echo', 'banner', 'guard', 'fury'],
  armor: ['thorns', 'guard', 'lifesteal'],
  boots: ['swift', 'thorns', 'echo'],
  amulet: ['burn', 'lifesteal', 'thorns', 'swift', 'fury', 'execute', 'chain', 'frost', 'cleave', 'guard', 'echo', 'banner'],
  ring: ['burn', 'lifesteal', 'thorns', 'swift', 'fury', 'execute', 'chain', 'frost', 'cleave', 'guard', 'echo', 'banner'],
};

/** Head, chest and feet all of one weight: their three traits count `bp` more. Two rings of one form: the second's counts `bp`. */
export const WEIGHT_SET_BP = 5000;
export const SAME_RING_BP = 5000;

/**
 * Item power (docs/EQUIPO.md §4): the level of whoever gets the piece plus what its place adds. The
 * stretched attributes roll in their rarity's range × (1 + `stepBp` × (power − 1)). A piece can be worn
 * from level power − `slack`. From `veryRareFrom` on, `veryRareBp` of the drops are very rare (taken from rare).
 */
export const POWER = { stepBp: 300, slack: 8, veryRareFrom: 20, veryRareBp: 200 };
/** What each place adds to a piece's power. */
export const LOOT_POWER = {
  incursion: [0, 0, 2, 4] as number[],
  lair: 2,
  crypt: 1,
  cryptAscuas: 3,
  deepCrypt: 4,
  deepFloor: 1,
  cellars: 3,
  mazeChest: 2,
  mazeCenter: 5,
  /** Warlords and bosses of the bastion: one per Marea past the sixth. */
  deepMarea: 1,
};
/** How much a power stretches a roll (bp). */
export const powerBp = (power: number): number => 10000 + POWER.stepBp * Math.max(0, power - 1);

/** How likely each slot is on a drop (a ring from SLOT_LEVEL.ring on); slots the hero hasn't opened yet are left out. */
export const SLOT_DROP_WEIGHT: Record<GearSlot, number> = { weapon: 22, offhand: 10, head: 15, armor: 18, boots: 15, amulet: 20, ring: 12 };

/** The rarities a drop can roll, with very rare among them (see POWER). */
export const ROLLED_RARITIES: Rarity[] = ['common', 'uncommon', 'rare', 'veryRare'];

/** The damage type a talisman or a ward ring guards against, rolled on the piece. */
export type TraitRes = AffinityType;

/** A blocked blow (a shield's trait, GearTrait.blockBp) lands for this share. */
export const BLOCK_TAKEN_BP = 5000;

/*
 * Settings and gems (docs/EQUIPO.md §5). Every piece has settings, as many as its slot and rarity allow; each
 * one opened costs more and asks for a level. Gems are cut from the storage's crystal and fused three into
 * one of the next cut; a gem set in a piece adds its cut's value times the piece's budget, and all the gems
 * of one attribute add up to `capBp` at most. Taking a gem out keeps it.
 */
/** The most settings a slot holds (a two-handed weapon one more), for very rare and unique pieces. */
export const SOCKETS_BY_SLOT: Record<GearSlot, number> = { weapon: 2, offhand: 1, head: 1, armor: 2, boots: 1, amulet: 2, ring: 1 };
export const SOCKETS_TWO_HANDED = 3;
/** Opening the first, second and third setting of a piece: dust from the storage, the hero's embers and shards, and a level. */
export const SOCKET_OPEN = [
  { level: 5, dust: 50, embers: 0, shards: 0 },
  { level: 12, dust: 150, embers: 5, shards: 0 },
  { level: 25, dust: 400, embers: 0, shards: 10 },
];
/** The cuts, from chipped to the titan's: what each adds (a whole slot's worth) and from what level it can be made. */
export type GemCut = 0 | 1 | 2 | 3 | 4;
export const GEM_CUTS: GemCut[] = [0, 1, 2, 3, 4];
export const GEM_CUT_KEYS = ['chipped', 'whole', 'polished', 'perfect', 'titan'] as const;
export const GEM_CUT_BP = [400, 800, 1000, 1200, 1400];
export const GEM_CUT_LEVEL = [5, 5, 20, 30, 40];
/** Cutting a gem straight from crystal: chipped, or whole (its price since settings came, 2026-10-25). */
export const GEM_CUT_COST: Partial<Record<GemCut, { crystal: number; dust: number }>> = { 0: { crystal: 30, dust: 20 }, 1: { crystal: 80, dust: 60 } };
/** Fusing three gems of a cut into one of the next: dust, and the hero's embers, shards or a deep Tide's seal for the high cuts. */
export const GEM_FUSE: Record<1 | 2 | 3 | 4, { dust: number; embers: number; shards: number; tideSeal: number }> = {
  1: { dust: 20, embers: 0, shards: 0, tideSeal: 0 },
  2: { dust: 100, embers: 3, shards: 0, tideSeal: 0 },
  3: { dust: 200, embers: 0, shards: 5, tideSeal: 0 },
  4: { dust: 400, embers: 0, shards: 0, tideSeal: 1 },
};
/** All the gems of one attribute on a hero add this much at most (three whole rubies' worth: the most one could set before). */
export const GEM_CAP_BP = 2400;

/*
 * Loot with a destination (docs/EQUIPO.md §6): some of what falls is for another class of the group (to give
 * away), each place drops more of one slot, and each campaign day favours one base (shown with the omen).
 */
/** Of each piece with a class, this much is for a class drawn among the group's (the recipient's own among them). */
export const OTHER_CLASS_BP = 3000;
export type LootPlace = 'incursion' | 'lair' | 'crypt' | 'deepCrypt' | 'cellars' | 'maze' | 'tide' | 'sima';
/** The slot each place drops more of, and by how much its weight grows. */
export const LOOT_FAVOR: Record<LootPlace, GearSlot> = { incursion: 'weapon', lair: 'offhand', crypt: 'armor', deepCrypt: 'boots', cellars: 'head', maze: 'amulet', tide: 'ring', sima: 'boots' };
export const LOOT_FAVOR_BP = 20000;
/** The day's base weighs this much more when its slot is drawn. */
export const DAY_BASE_BP = 30000;

/*
 * The long stretch (docs/EQUIPO.md §7): what levels 38 to 50 open at the forge, and the sets.
 */
/** Directed reforging (level 38): the player picks what the attribute becomes (rolled anew), at this much of a reforge's dust. */
export const DIRECTED_REFORGE = { level: 38, costBp: 30000 };
/** A masterwork (level 48): a very rare piece's attributes all rise to the top of their range. Once per hero. */
export const MASTERWORK = { level: 48, dust: 1200, embers: 15, shards: 15 };
/** The titan's mark (level 50): the miniature wears its own glow. */
export const TITAN_MARK_LEVEL = 50;

/**
 * Sets (docs/EQUIPO.md §7): three pieces from one place, dropping one at a time there from a level on. Two
 * worn give a small bonus, all three an effect at full strength. Set pieces can be given away like any other.
 */
export type SetId = 'garrison' | 'depths' | 'furrows' | 'tide';
export const SET_IDS: SetId[] = ['garrison', 'depths', 'furrows', 'tide'];
export interface GearSetDef {
  place: LootPlace;
  level: number;
  /** Its pieces' slots (the tide's pair of rings: two rings). */
  slots: GearSlot[];
  two: GearTrait;
  three: GearTrait;
  effect: GearEffect;
}
export const GEAR_SETS: Record<SetId, GearSetDef> = {
  garrison: { place: 'cellars', level: 18, slots: ['head', 'armor', 'boots'], two: { hpBp: 1000 }, three: { armorBp: 600 }, effect: 'guard' },
  depths: { place: 'deepCrypt', level: 28, slots: ['weapon', 'head', 'amulet'], two: { damageBp: 800 }, three: { critBp: 400 }, effect: 'execute' },
  furrows: { place: 'maze', level: 33, slots: ['boots', 'amulet', 'ring'], two: { attackSpeedBp: 800 }, three: { speedBp: 600 }, effect: 'echo' },
  tide: { place: 'tide', level: 42, slots: ['ring', 'ring', 'amulet'], two: { critBp: 600 }, three: { damageBp: 600 }, effect: 'chain' },
};
/** Of what drops at a set's place (from its level), this much is one of its pieces (rare at least). */
export const SET_DROP_BP = 2000;
